import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import * as Yup from "yup";
import dayjs from "dayjs";
import { Modal, Input } from "antd";
import FullPageLoader from "../../../core/common/loader/FullPageLoader";
import {
    fetchReimbursementTypes,
    fetchRequestTypes,
    fetchLTCSchemes,
    fetchBlockTypes,
    fetchDynamicConfig,
    createReimbursementRequest,
    fetchReimbursementById,
    fetchReimbursementMasters,
    fetchReimbursementAssocRef,
    syncReimbursementRequest,
    syncReimbursementTypes,
    DynamicField,
    BlockType,
} from "../../../core/data/redux/reimbursementSlice";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";

// Sub-components
import { ReimbursementFormData, AttachmentItem, Dependant } from "./components/types";
import { ReimbursementFormHeader } from "./components/ReimbursementFormHeader";
import { DynamicFieldsSection } from "./components/DynamicFieldsSection";
import { DependentsModal } from "./components/DependentsModal";
import { LineItemsSection } from "./components/LineItemsSection";
import { LineItemsModal } from "./components/LineItemsModal";
import { AttachmentsAndRemarksSection } from "./components/AttachmentsAndRemarksSection";
import {
    isBoolean,
    parseMsDate,
    isDateType,
    isTimeType,
    FieldType,
    isInvalidDefaultValue,
    isProfileField,
    isEditableUserEntryField,
} from "./components/dynamicFieldUtils";

// Yup validation schema
const validationSchema = Yup.object().shape({
    ReimbursementType: Yup.string().required("Reimbursement Type is required"),
});

const ReimbursementCreationModal: React.FC = () => {
    const routes = all_routes;
    const navigate = useNavigate();
    useParams<{ id: string }>();
    const dispatch = useDispatch<any>();

    const {
        error,
        reimbursementTypes,
        reimbursementTypesLoading,
        requestTypes,
        requestTypesLoading,
        ltcSchemes,
        ltcSchemesLoading,
        blockTypes,
        blockTypesLoading,
        dynamicConfig,
        dynamicConfigLoading,
        reimbursementMasters,
        mastersLoading,
        associatedRefData,
        associatedRefLoading,
    } = useSelector((state: any) => state.reimbursementState);

    const [formData, setFormData] = useState<ReimbursementFormData>({
        ReimbursementType: "",
        RequestType: "",
        LTCScheme: "",
        BlockType: "",
        AssociatedReference: "",
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Processing...");
    const [showResponseModal, setShowResponseModal] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    const [isReviewMode, setIsReviewMode] = useState(false);
    const [reviewData, setReviewData] = useState<any>(null);

    const [showDependentsModal, setShowDependentsModal] = useState(false);
    const [selectedDependents, setSelectedDependents] = useState<Dependant[]>([]);
    const [isSyncingDependents, setIsSyncingDependents] = useState(false);
    const [isSyncingTypes, setIsSyncingTypes] = useState(false);

    const [showLineItemModal, setShowLineItemModal] = useState(false);
    const [lineItems, setLineItems] = useState<any[]>([]);
    const [currentLineItem, setCurrentLineItem] = useState<any>(null);
    const [editingLineItemIndex, setEditingLineItemIndex] = useState<number | null>(null);

    const [remark, setRemark] = useState("");
    const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
    const [searchParams] = useSearchParams();
    const [userData, setUserData] = useState<any>(null);

    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
    const [refLtcBlockDates, setRefLtcBlockDates] = useState<{ startDate: string; endDate: string } | null>(null);
    const [refDependents, setRefDependents] = useState<any[] | null>(null);

    // Determine if the requested value should be treated as a Number instead of an Amount
    const tableHeaders = dynamicConfig?.configData?.parsedMultilineTableHeaders || dynamicConfig?.parsedMultilineTableHeaders || [];
    const isNumberField = Array.isArray(tableHeaders) && tableHeaders.some((item: any) => item?.LineCustomerField === 'RQNUM');
    const requestedLabel = isNumberField ? "Requested Number" : "Requested Amount";
    const useAmountAtLine = Boolean(dynamicConfig?.configData?.UseAmountAtLine ?? dynamicConfig?.UseAmountAtLine);

    const parsedStaticFields = dynamicConfig?.configData?.parsedStaticFields || dynamicConfig?.parsedStaticFields || [];
    const hasVisibleStaticFields = Array.isArray(parsedStaticFields) && parsedStaticFields.length > 0
        ? parsedStaticFields.some((field: any) => field?.Visible && field?.Enable)
        : true;

    // Matching mobile app logic: ZLTA's AP and AC request types don't show Static Fields (Requested Amount etc.)
    const excludeStaticFields =
        (formData.ReimbursementType === "ZLTA" && (formData.RequestType === "AP" || formData.RequestType === "AC")) ||
        !hasVisibleStaticFields;

    const getLineAmountFieldKey = () => {
        if (Array.isArray(tableHeaders)) {
            if (tableHeaders.some((f: any) => (f?.LineCustomerField || f?.HeaderCustomerField) === 'RQAMT')) return 'RQAMT';
            if (tableHeaders.some((f: any) => (f?.LineCustomerField || f?.HeaderCustomerField) === 'RQNUM')) return 'RQNUM';
            const amtField = tableHeaders.find((f: any) => {
                const key = f?.LineCustomerField || f?.HeaderCustomerField || '';
                return key.startsWith('RQAMT') || key.startsWith('RQNUM') || key.startsWith('AMT');
            });
            if (amtField) return amtField.LineCustomerField || amtField.HeaderCustomerField;
        }
        return 'RQAMT';
    };

    const updateLineItemsAndTotal = (newItems: any[]) => {
        setLineItems(newItems);
        if (newItems.length > 0) {
            setValidationErrors((prev) => {
                const updated = { ...prev };
                delete updated.lineItems;
                return updated;
            });
        }
        if (useAmountAtLine) {
            const amountFieldKey = getLineAmountFieldKey();
            const total = newItems.reduce((sum, line: any) => {
                const val = parseFloat(line?.[amountFieldKey] ?? '0');
                return sum + (isNaN(val) ? 0 : val);
            }, 0);
            setFormData((prev: ReimbursementFormData) => ({
                ...prev,
                RequestedAmount: newItems.length > 0 ? (isNumberField ? String(total) : total.toFixed(2)) : "",
            }));
        }
    };

    // Load initial types and user details
    useEffect(() => {
        dispatch(fetchReimbursementTypes());
        dispatch(fetchReimbursementMasters());

        const storedUserData = localStorage.getItem("userData") || localStorage.getItem("user");
        if (storedUserData) {
            try {
                setUserData(JSON.parse(storedUserData));
            } catch (e) {
                console.error("Error parsing user data:", e);
            }
        }
    }, [dispatch]);

    // Handle query parameters from list page
    useEffect(() => {
        const typeParam = searchParams.get("reimbursementType");
        const reqParam = searchParams.get("requestType");

        if (typeParam) {
            setFormData((prev: ReimbursementFormData) => ({ ...prev, ReimbursementType: typeParam }));
            setAttachments([]);
            setRemark("");
            dispatch(fetchRequestTypes(typeParam));
        }
        if (reqParam) {
            setFormData((prev: ReimbursementFormData) => ({ ...prev, RequestType: reqParam }));
        }
        if (typeParam && reqParam) {
            const currentEmpId = userData?.details?.empId || userData?.empId || userData?.usrid || "";
            const isAssocStageNow =
                (typeParam === 'ZLTA' && reqParam !== 'AP') ||
                ((typeParam === 'ZTDA' || typeParam === 'ZVAQ') && (reqParam === 'CA' || reqParam === 'CC'));

            if (isAssocStageNow) {
                dispatch(fetchReimbursementAssocRef({
                    empId: currentEmpId,
                    reimbursementType: typeParam,
                    ltcSchemeCode: "",
                    forceSync: true
                }));
            }

            if (!((typeParam === 'ZTDA' || typeParam === 'ZVAQ') && reqParam === 'CA')) {
                dispatch(
                    fetchDynamicConfig({
                        ReferenceNumber: "",
                        ReimbursementType: typeParam,
                        RequestType: reqParam,
                        LTCSchemeCode: "",
                        EmpID: currentEmpId,
                        RequestOperation: "EC",
                    })
                );
            }
        }
    }, [searchParams, dispatch, userData]);

    const handleInputChange = (e: { target: { name: string; value: any } }) => {
        const { name, value } = e.target;

        if (name === "ReimbursementType") {
            setFormData({
                ReimbursementType: value,
                RequestType: "",
                LTCScheme: "",
                BlockType: "",
                AssociatedReference: "",
            });
            setSelectedDependents([]);
            setLineItems([]);
            setValidationErrors({});
            setAttachments([]);
            setRemark("");
            setRefLtcBlockDates(null);
            setRefDependents(null);
            if (value) {
                dispatch(fetchRequestTypes(value));
            }
        } else if (name === "RequestType") {
            setFormData({
                ReimbursementType: formData.ReimbursementType,
                RequestType: value,
                LTCScheme: "",
                BlockType: "",
                AssociatedReference: "",
            });
            setSelectedDependents([]);
            setLineItems([]);
            setValidationErrors({});
            setAttachments([]);
            setRemark("");
            setRefLtcBlockDates(null);
            setRefDependents(null);

            if (value && formData.ReimbursementType === "ZLTA") {
                dispatch(fetchLTCSchemes(value));
            }

            if (value && formData.ReimbursementType) {
                const currentEmpId = userData?.details?.empId || userData?.empId || userData?.usrid || "";

                const isAssocStageNow =
                    (formData.ReimbursementType === 'ZLTA' && value !== 'AP') ||
                    ((formData.ReimbursementType === 'ZTDA' || formData.ReimbursementType === 'ZVAQ') && (value === 'CA' || value === 'CC'));

                if (isAssocStageNow) {
                    dispatch(fetchReimbursementAssocRef({
                        empId: currentEmpId,
                        reimbursementType: formData.ReimbursementType,
                        ltcSchemeCode: "",
                        forceSync: true
                    }));
                }

                if (!((formData.ReimbursementType === 'ZTDA' || formData.ReimbursementType === 'ZVAQ') && value === 'CA')) {
                    dispatch(
                        fetchDynamicConfig({
                            ReferenceNumber: "",
                            ReimbursementType: formData.ReimbursementType,
                            RequestType: value,
                            LTCSchemeCode: "",
                            EmpID: currentEmpId,
                            RequestOperation: "EC",
                        })
                    );
                }
            }
        } else if (name === "LTCScheme") {
            setFormData({
                ReimbursementType: formData.ReimbursementType,
                RequestType: formData.RequestType,
                LTCScheme: value,
                BlockType: "",
                AssociatedReference: "",
            });
            setSelectedDependents([]);
            setLineItems([]);
            setValidationErrors({});
            setAttachments([]);
            setRemark("");
            setRefLtcBlockDates(null);
            setRefDependents(null);

            if (value) {
                dispatch(fetchBlockTypes(value));
            }

            if (value && formData.ReimbursementType && formData.RequestType) {
                const currentEmpId = userData?.details?.empId || userData?.empId || userData?.usrid || "";

                const isAssocStageNow =
                    (formData.ReimbursementType === 'ZLTA' && formData.RequestType !== 'AP') ||
                    ((formData.ReimbursementType === 'ZTDA' || formData.ReimbursementType === 'ZVAQ') && (formData.RequestType === 'CA' || formData.RequestType === 'CC'));

                if (isAssocStageNow) {
                    dispatch(fetchReimbursementAssocRef({
                        empId: currentEmpId,
                        reimbursementType: formData.ReimbursementType,
                        ltcSchemeCode: value,
                        forceSync: true
                    }));
                }

                if (!((formData.ReimbursementType === 'ZTDA' || formData.ReimbursementType === 'ZVAQ') && formData.RequestType === 'CA')) {
                    dispatch(
                        fetchDynamicConfig({
                            ReferenceNumber: "",
                            ReimbursementType: formData.ReimbursementType,
                            RequestType: formData.RequestType,
                            LTCSchemeCode: value,
                            EmpID: currentEmpId,
                            RequestOperation: "EC",
                        })
                    );
                }
            }
        } else if (name === "AssociatedReference") {
            setFormData((prev: ReimbursementFormData) => ({
                ...prev,
                AssociatedReference: value,
            }));
            setSelectedDependents([]);
            setLineItems([]);
            setValidationErrors({});
            setAttachments([]);
            setRemark("");
            setRefLtcBlockDates(null);
            setRefDependents(null);

            const currentEmpId = userData?.details?.empId || userData?.empId || userData?.usrid || "";

            if ((formData.ReimbursementType === 'ZTDA' || formData.ReimbursementType === 'ZVAQ') && formData.RequestType === 'CA') {
                dispatch(
                    fetchDynamicConfig({
                        ReferenceNumber: value,
                        ReimbursementType: formData.ReimbursementType,
                        RequestType: formData.RequestType,
                        LTCSchemeCode: formData.LTCScheme || "",
                        EmpID: currentEmpId,
                        RequestOperation: "EC",
                    })
                );
            }

            // Fetch referenced application to merge ToDynamics, ToDependants, and LTCBlockDates (matching mobile mergeDynamicFieldValuesFromRef)
            if (value && (formData.ReimbursementType === 'ZLTA' || formData.ReimbursementType === 'ZTDA' || formData.ReimbursementType === 'ZVAQ')) {
                dispatch(fetchReimbursementById(value)).unwrap().then((refDetails: any) => {
                    if (!refDetails) return;
                    const toDynamics = refDetails?.ToDynamics?.[0];
                    if (toDynamics) {
                        const parsedDynFields: any[] =
                            dynamicConfig?.configData?.parsedDynamicFields ||
                            dynamicConfig?.parsedDynamicFields ||
                            [];

                        const fieldKeys = parsedDynFields.length > 0
                            ? parsedDynFields.map((f: any) => f.HeaderCustomerField).filter(Boolean)
                            : Object.keys(toDynamics);

                        setFormData((prev: ReimbursementFormData) => {
                            const updated = { ...prev };
                            fieldKeys.forEach((key: string) => {
                                if (toDynamics[key] !== undefined) {
                                    updated[key] = toDynamics[key] ?? '';
                                }
                            });
                            return updated;
                        });
                    }

                    // For ZLTA, resolve pre-selected dependents from referenced application's ToDependants
                    if (formData.ReimbursementType === 'ZLTA' && formData.RequestType === 'AC') {
                        const refDeps: any[] = refDetails?.ToDependants || [];
                        if (refDeps.length > 0) {
                            setRefDependents(refDeps);
                        }
                        const preSelected = refDeps.filter((d: any) => d?.DependentSelected === true);
                        if (preSelected.length > 0) {
                            setSelectedDependents(preSelected);
                        }

                        // Extract LTCBlockBeginDate and LTCBlockEndDate
                        const parseODataDate = (val: any): string | null => {
                            if (!val) return null;
                            const match = typeof val === 'string' ? val.match(/\/Date\((-?\d+)\)\//) : null;
                            return match ? new Date(parseInt(match[1], 10)).toISOString() : null;
                        };
                        const ltcStart = parseODataDate(refDetails?.LTCBlockBeginDate);
                        const ltcEnd = parseODataDate(refDetails?.LTCBlockEndDate);
                        if (ltcStart && ltcEnd) {
                            setRefLtcBlockDates({ startDate: ltcStart, endDate: ltcEnd });
                        }
                    }
                }).catch((err: any) => {
                    console.error("Error fetching referenced reimbursement by id:", err);
                });
            }
        } else {
            setFormData((prev: ReimbursementFormData) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const extractYearsFromBlockType = (blockType: BlockType): number => {
        if (!blockType) return 4;
        if (blockType.years && typeof blockType.years === "number") return blockType.years;
        if (blockType.block_years && typeof blockType.block_years === "number") return blockType.block_years;

        const blockTypeName = String(blockType.ltc_block_type || (blockType as any).name || "");
        if (typeof blockTypeName === "string" && typeof blockTypeName.match === "function") {
            const yearMatch = blockTypeName.match(/(\d+)\s*(?:year|yr|y)/i);
            if (yearMatch && yearMatch[1]) {
                const parsed = parseInt(yearMatch[1], 10);
                if (!isNaN(parsed) && parsed > 0) return parsed;
            }
        }
        return 4;
    };

    const parseDateToDMY = (val: any): string => {
        if (!val) return "";
        if (typeof val === "string" && val.includes("/Date(")) {
            const ms = parseInt(val.replace(/\/Date\((.*?)\)\//, "$1"), 10);
            if (!isNaN(ms)) {
                const d = new Date(ms);
                return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
            }
        }
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
            return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
        }
        return String(val);
    };

    const getBlockTypeDateRange = (blockType: BlockType): string => {
        const rawStart = (blockType as any).StartDate || (blockType as any).start_date || (blockType as any).startDate;
        const rawEnd = (blockType as any).EndDate || (blockType as any).end_date || (blockType as any).endDate;

        let dateRangeStr = "";
        if (rawStart && rawEnd) {
            dateRangeStr = `${parseDateToDMY(rawStart)} - ${parseDateToDMY(rawEnd)}`;
        } else {
            const endDate = new Date(2029, 11, 31);
            const years = extractYearsFromBlockType(blockType);
            const startDate = new Date(endDate.getFullYear() - years + 1, 0, 1);

            const formatDateStr = (d: Date) =>
                `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

            dateRangeStr = `${formatDateStr(startDate)} - ${formatDateStr(endDate)}`;
        }

        const selectedScheme = (ltcSchemes || []).find(
            (s: any) => s.ltc_scheme_code === formData.LTCScheme || s.ltc_scheme_code === blockType.ltc_scheme_code
        );

        let labelPrefix = blockType.ltc_block_type || "";
        const schemeCode = formData.LTCScheme || blockType.ltc_scheme_code;
        if (schemeCode === "HT") {
            labelPrefix = "Home Town";
        } else if (schemeCode === "AI" || selectedScheme?.ltc_scheme_text === "All India") {
            labelPrefix = "All India";
        } else if (selectedScheme?.ltc_scheme_text) {
            labelPrefix = selectedScheme.ltc_scheme_text;
        }

        return `${labelPrefix}: ${dateRangeStr}`;
    };

    const validateForm = async () => {
        try {
            await validationSchema.validate(formData, { abortEarly: false });

            const errors: { [key: string]: string } = {};
            const parsedFields = dynamicConfig?.configData?.parsedDynamicFields || dynamicConfig?.parsedDynamicFields || [];

            if (parsedFields && Array.isArray(parsedFields)) {
                parsedFields.forEach((field: DynamicField) => {
                    const label = field.Label || "";
                    const labelLower = label.toLowerCase().trim();
                    const keyLower = (field.HeaderCustomerField || "").toLowerCase().trim();

                    const isActualBillField =
                        labelLower.includes("actual bill") ||
                        labelLower.includes("bill amount") ||
                        keyLower.includes("act_bill") ||
                        keyLower.includes("actualbill");

                    const isRequired =
                        field.CustFldMandatory === true ||
                        String(field.CustFldMandatory) === "true" ||
                        isActualBillField;

                    const isVisible = field.VisibleToEmployee === true || String(field.VisibleToEmployee).toLowerCase() === "true";
                    const fieldName = field.HeaderCustomerField;

                    if (isVisible && fieldName) {
                        const val = formData[fieldName];

                        // Required validation
                        if (isRequired && (val === undefined || val === null || String(val).trim() === "")) {
                            errors[fieldName] = `${field.Label || "Actual Bill Amount"} is required`;
                        } else if (val) {
                            // Mobile/phone number 10-digit validation
                            if (labelLower.includes("mobile") || labelLower.includes("phone") || labelLower.includes("moblie")) {
                                const digitsOnly = String(val).replace(/\D/g, "");
                                if (digitsOnly.length !== 10) {
                                    errors[fieldName] = `${field.Label} must be exactly 10 digits`;
                                }
                            } else if (labelLower.includes("pan")) {
                                const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i;
                                if (!panRegex.test(String(val))) {
                                    errors[fieldName] = `Please enter a valid PAN number`;
                                }
                            } else if (labelLower.includes("asset") || keyLower.includes("asset")) {
                                const maxLen = parseInt(field.WidthOnESSScreen || "0", 10) || 16;
                                if (String(val).length > maxLen) {
                                    errors[fieldName] = `${field.Label} must not exceed ${maxLen} characters`;
                                }
                            }
                        }
                    }
                });
            }

            const termConditions: any[] =
                dynamicConfig?.configData?.parsedTermCondition ??
                dynamicConfig?.parsedTermCondition ??
                [];

            if (termConditions.length > 0 && !formData.acceptTerms) {
                errors.acceptTerms = "Please acknowledge and accept the Terms & Conditions before submitting.";
            }

            // ── Dependents Validation (matching mobile DetailsTab.tsx / CreateReimbursementScreen.tsx) ──
            if (allowDependants && selectedDependents.length === 0) {
                errors.selectedDependents = "Please select at least one dependent.";
            }

            // ── MultiLine Table Line Items Validation (matching mobile hasMissingFields: (!isMultiLineEmpty && formDataToAddLine.length === 0)) ──
            const multilineHeaders = dynamicConfig?.configData?.parsedMultilineTableHeaders || dynamicConfig?.parsedMultilineTableHeaders || [];
            const visibleMultilineHeaders = multilineHeaders.filter(
                (header: DynamicField) => isBoolean(header.VisibleToEmployee)
            );

            if (visibleMultilineHeaders.length > 0) {
                if (lineItems.length === 0) {
                    errors.lineItems = "Please add at least one line item.";
                } else {
                    const mandatoryHeaders = visibleMultilineHeaders.filter(
                        (h: any) => isBoolean(h.CustFieldMandatory) || isBoolean(h.CustFldMandatory)
                    );
                    const hasIncompleteLine = lineItems.some((item) =>
                        mandatoryHeaders.some((h: any) => {
                            const fieldName = (h.LineCustomerField || h.HeaderCustomerField || "") as string;
                            const val = item[fieldName];
                            return val === undefined || val === null || String(val).trim() === "";
                        })
                    );
                    if (hasIncompleteLine) {
                        errors.lineItems = "One or more line items have missing required fields. Please edit and complete them.";
                    }
                }
            }

            if (Object.keys(errors).length > 0) {
                setValidationErrors(errors);
                return { isValid: false, errors };
            }

            setValidationErrors({});
            return { isValid: true };
        } catch (error: any) {
            const errors: { [key: string]: string } = {};
            if (error.inner) {
                error.inner.forEach((err: any) => {
                    if (err.path) errors[err.path] = err.message;
                });
            }
            setValidationErrors(errors);
            return { isValid: false, errors };
        }
    };

    const formatMicrosoftJsonDate = (dateStr: any) => {
        if (!dateStr) return null;
        if (typeof dateStr === "string") {
            const match = dateStr.match(/\/Date\((-?\d+)\)\//);
            if (match) {
                const d = new Date(parseInt(match[1], 10));
                if (isNaN(d.getTime())) return null;
                const ms = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
                return `/Date(${ms})/`;
            }
        }
        const parsed = dayjs(dateStr, ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "DD.MM.YYYY", "YYYYMMDD"]);
        if (parsed.isValid()) {
            const ms = Date.UTC(parsed.year(), parsed.month(), parsed.date());
            return `/Date(${ms})/`;
        }
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
            const ms = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
            return `/Date(${ms})/`;
        }
        return null;
    };

    const buildSubmissionObject = async (action: string = "C") => {
        // Mobile app sends raw empId without zero-padding – match that behaviour
        const empId = userData?.empId || userData?.usrid || "";
        const userId = userData?.usrid || empId || "";

        const allMasterDependents =
            (formData.ReimbursementType === "ZLTA" && formData.RequestType === "AC" && refDependents && refDependents.length > 0)
                ? refDependents
                : (
                    dynamicConfig?.configData?.parsedToDependants ||
                    dynamicConfig?.parsedToDependants ||
                    dynamicConfig?.configData?.toDependants ||
                    dynamicConfig?.toDependants ||
                    reimbursementMasters?.configData?.parsedToDependants ||
                    reimbursementMasters?.toDependants ||
                    []
                );

        const selectedKeys = new Set(selectedDependents.map((d: Dependant) => d.FamsaObjps));

        let cleanedDependents: any[] = [];
        if (formData.ReimbursementType === "ZLTA") {
            // ZLTA only: send every dependent the employee was shown, not just
            // the ones they checked — DependentSelected true/false per row (matches mobile).
            if (Array.isArray(allMasterDependents) && allMasterDependents.length > 0) {
                cleanedDependents = allMasterDependents.map((dependent: any) => {
                    const { EmpID, empId, FamilyMemberText, WidowMotherFlag, ...rest } = dependent;
                    return {
                        ...rest,
                        DependentSelected: selectedKeys.has(rest.FamsaObjps),
                    };
                });
            }
        } else if (Array.isArray(selectedDependents) && selectedDependents.length > 0) {
            cleanedDependents = selectedDependents.map((dependent: any) => {
                const { EmpID, empId, FamilyMemberText, WidowMotherFlag, ...rest } = dependent;
                return rest;
            });
        }

        const toDependants = {
            results: cleanedDependents,
        };

        const remarkText = remark.trim();
        const userDetails = userData?.details || userData;
        const employeeName = [
            userDetails?.firstname || userData?.firstname,
            userDetails?.lsastname || userDetails?.lastname || userData?.lastname
        ].filter(Boolean).join(" ").trim() || (userData?.name || "EMPLOYEE");

        const toRemarks = {
            results: [
                {
                    LineNumber: "0000",
                    CommentBy: "E",
                    CommentedByUserID: userId,
                    ApproverLevel: "00",
                    EmployeeName: employeeName,
                    Comment: remarkText,
                    PhaseOfTheRequest1: "N",
                    RequestStatus1: "N",
                    PhaseOfTheRequest2: "P",
                    RequestStatus2: "T",
                    Checkbox: true,
                }
            ],
        };

        const toAttachments = {
            results: await Promise.all(
                attachments.map(async (att, index) => {
                    const base64Content = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(att.file);
                        reader.onload = () => resolve((reader.result as string).split(",")[1]);
                        reader.onerror = (err) => reject(err);
                    });

                    const ext = att.name.split(".").pop()?.toUpperCase() || "";
                    return {
                        Content: base64Content,
                        FileExtensionDesc: "Documents",
                        LineNumber: String(index + 1).padStart(4, "0"),
                        FileName: att.name,
                        FileExtension: ext || "UNKNOWN",
                        FileSize: `${Math.round(att.size / 1024)}KB`,
                    };
                })
            ),
        };

        const toDynamics: any = {};
        const dynamicFields = dynamicConfig?.configData?.parsedDynamicFields || dynamicConfig?.parsedDynamicFields || [];

        const userPayScale =
            dynamicConfig?.employeeDetails?.pay_scale ||
            dynamicConfig?.pay_scale ||
            userData?.details?.pay_scale ||
            userData?.pay_scale ||
            "0.00";

        const userSalary =
            dynamicConfig?.employeeDetails?.basicSalary ||
            dynamicConfig?.basicSalary ||
            userData?.details?.basicSalary ||
            userData?.basicSalary ||
            "0.00";

        const empType = dynamicConfig?.employeeDetails?.employmentType ?? userData?.details?.employmentType ?? "";
        const empStatus = dynamicConfig?.employeeDetails?.empStatus ?? userData?.details?.empStatus ?? "";

        dynamicFields.forEach((field: DynamicField) => {
            const fieldName = field.HeaderCustomerField || "";
            if (fieldName) {
                let val = formData[fieldName];
                const labelLower = (field.Label || "").toLowerCase().trim();
                const isReadOnly = isBoolean(field.ReadOnlyForEmp);

                if (labelLower.includes("pay scale")) {
                    val = userPayScale || field.DefaultValue || "0.00";
                } else if (labelLower.includes("basic salary")) {
                    val = userSalary || field.DefaultValue || "0.00";
                } else if (labelLower.includes("recruitment") || labelLower.includes("employment status")) {
                    val = ((empType || empStatus) ? `${empType}-${empStatus}` : "") || field.DefaultValue || "-";
                } else if (isReadOnly) {
                    const rawDef = field.DefaultValue ?? "";
                    val = (val !== undefined && val !== null && val !== "")
                        ? val
                        : (!isInvalidDefaultValue(field, rawDef, userPayScale, userSalary) ? rawDef : "");
                } else {
                    // Editable field: val strictly comes from user entry (formData[fieldName]).
                    // If empty, undefined, null, or matching an invalid leaked default: send null (matches mobile finalizeToDynamicsObject).
                    // CRITICAL: NEVER fall back to field.DefaultValue!
                    if (val === undefined || val === null || val === "" || isInvalidDefaultValue(field, val, userPayScale, userSalary)) {
                        val = null;
                    }
                }

                const isFieldCheckbox =
                    field.ViewOfFieldType === "C" ||
                    fieldName.startsWith("CKB") ||
                    fieldName === "CLMCF" ||
                    fieldName === "SLFTR" ||
                    typeof val === "boolean" ||
                    val === "true" ||
                    val === "false";

                if (isFieldCheckbox) {
                    val = val === true || val === "true" || val === "YES" || val === "X" || val === 1 || val === "1";
                } else if (isDateType((field.ViewOfFieldType || "") as FieldType, fieldName, field.Label)) {
                    val = val ? formatMicrosoftJsonDate(val) : null;
                } else if (fieldName.startsWith("AMT") || fieldName.startsWith("RQAMT")) {
                    if (val !== null && val !== undefined && val !== "") {
                        const parsed = parseFloat(String(val).replace(/,/g, ""));
                        val = isNaN(parsed) || parsed <= 0 ? (isReadOnly ? "0.00" : null) : parsed.toFixed(2);
                    } else {
                        val = isReadOnly ? "0.00" : null;
                    }
                } else {
                    if (val === "" || val === undefined || val === null) {
                        val = null; // Match mobile finalizeToDynamicsObject: strictly convert empty to null
                    } else if (typeof val !== "boolean") {
                        val = String(val);
                    }
                }
                toDynamics[fieldName] = val;
            }
        });

        const tableHeaders = dynamicConfig?.configData?.parsedMultilineTableHeaders || dynamicConfig?.parsedMultilineTableHeaders || [];
        const toMultiLines = {
            results: lineItems.map((item, index) => {
                const { FamilyMemberText, ...rest } = item;
                const lineItemObject: any = { ...rest };

                // Ensure LINUM is present and padded to 4 digits
                if (!lineItemObject.LINUM) {
                    lineItemObject.LINUM = String(index + 1).padStart(4, "0");
                }

                tableHeaders.forEach((header: DynamicField) => {
                    const fieldName = header.LineCustomerField || header.HeaderCustomerField || "";
                    if (fieldName) {
                        const isLineReadOnly =
                            isBoolean((header as any).CustFldReadOnly) ||
                            isBoolean(header.ReadOnlyForEmployee) ||
                            isBoolean(header.ReadOnlyForEmp);
                        const rawDef = header.DefaultValue ?? "";
                        const sanitizedDef = (!isInvalidDefaultValue(header, rawDef, userPayScale, userSalary) && isLineReadOnly) ? rawDef : "";

                        let val = item[fieldName] !== undefined && item[fieldName] !== null && item[fieldName] !== ""
                            ? item[fieldName]
                            : sanitizedDef;

                        const fieldType = (header.ViewOfFieldType || "") as FieldType;
                        const fieldLabel = header.Label || "";

                        const isLineCheckbox =
                            fieldType === "C" ||
                            fieldName.startsWith("CKB") ||
                            fieldName === "CLMCF" ||
                            fieldName === "SLFTR" ||
                            typeof val === "boolean" ||
                            val === "true" ||
                            val === "false";

                        if (isDateType(fieldType, fieldName, fieldLabel)) {
                            val = val ? formatMicrosoftJsonDate(val) : null;
                        } else if (isLineCheckbox) {
                            val = val === true || val === "true" || val === "YES" || val === "X" || val === 1 || val === "1";
                        } else if (fieldName.startsWith("AMT") || fieldName.startsWith("RQAMT") || fieldName.startsWith("RQNUM")) {
                            val = val ? String(val) : (isLineReadOnly ? "0.00" : null);
                        } else {
                            val = (val === "" || val === undefined) ? null : (typeof val === "boolean" ? val : String(val ?? ""));
                        }
                        lineItemObject[fieldName] = val;
                    }
                });

                // Ensure FGBDT is properly formatted as /Date(ms)/ if present
                if (lineItemObject.FGBDT) {
                    lineItemObject.FGBDT = formatMicrosoftJsonDate(lineItemObject.FGBDT);
                }

                return lineItemObject;
            }),
        };

        let ltcBlockBeginDateVal: string | null = null;
        let ltcBlockEndDateVal: string | null = null;
        if (formData.ReimbursementType === "ZLTA") {
            if (formData.BlockType) {
                const selectedBlock = (blockTypes || []).find((b: any, idx: number) => {
                    const optValue = b.block_key || b.id || b.ltc_block_type || String(idx);
                    return optValue === formData.BlockType || b.ltc_block_type === formData.BlockType || String(b.id) === String(formData.BlockType);
                });
                const rawStart = selectedBlock?.StartDate || selectedBlock?.start_date || selectedBlock?.startDate;
                const rawEnd = selectedBlock?.EndDate || selectedBlock?.end_date || selectedBlock?.endDate;

                if (rawStart && rawEnd) {
                    ltcBlockBeginDateVal = formatMicrosoftJsonDate(rawStart);
                    ltcBlockEndDateVal = formatMicrosoftJsonDate(rawEnd);
                } else {
                    const years = extractYearsFromBlockType(selectedBlock || { ltc_block_type: formData.BlockType });
                    const fallbackEndMs = Date.UTC(2029, 11, 31);
                    const fallbackStartMs = Date.UTC(2029 - years + 1, 0, 1);
                    ltcBlockBeginDateVal = `/Date(${fallbackStartMs})/`;
                    ltcBlockEndDateVal = `/Date(${fallbackEndMs})/`;
                }
            } else if (refLtcBlockDates) {
                ltcBlockBeginDateVal = formatMicrosoftJsonDate(refLtcBlockDates.startDate);
                ltcBlockEndDateVal = formatMicrosoftJsonDate(refLtcBlockDates.endDate);
            }
        }

        const toTransactionDatas = {
            // Mobile sends raw empId (no zero-padding)
            EmpID: empId,
            ReimbursementType: formData.ReimbursementType || "ZBRF",
            RequestType: formData.RequestType || "CL",
            RequestCreatedBy: empId,
            LTCSchemeCode: (formData.LTCScheme || formData.LTCSchemeCode) ? String(formData.LTCScheme || formData.LTCSchemeCode) : null,
            LTCBlockBeginDate: ltcBlockBeginDateVal,
            LTCBlockEndDate: ltcBlockEndDateVal,
            CreatedSource: null,
            ReasonCode: null,
            AppRejDelDate: null,
            InfotypeUpdateDate: null,
            PaymentDate: null,
            CompletedDate: null,
            InfotypeBeginDate: null,
            PhaseOftheRequest: "P",
            RequestStatus: "T",
            ReferenceNumber1: formData.AssociatedReference || null,
            TriggerRendering: null,
            TriggerRendering1: null,
            OffcycleRequested: true,
            TaxExemRequired: false,
            OffcycleProvided: true,
            DeclAgreement: true,
            PartialClaim: false,
            TripCompleted: false,
            PartNumber: null,
        };

        const isNumberField = Array.isArray(tableHeaders) && tableHeaders.some((item: any) => item?.LineCustomerField === 'RQNUM');

        const rawAmount = formData.RequestedAmount;

        const rawAmountStr = excludeStaticFields
            ? "0.00"
            : (rawAmount ? String(rawAmount).trim() : "0.00");

        let formattedAmountVal = "0.00";
        if (rawAmountStr && rawAmountStr !== "0.00") {
            const parsed = parseFloat(rawAmountStr.replace(/,/g, ""));
            formattedAmountVal = isNaN(parsed) || parsed <= 0 ? "0.00" : parsed.toFixed(2);
        }

        const requestedAmountVal = isNumberField ? "0.00" : formattedAmountVal;
        const requestedNumberVal = isNumberField
            ? (rawAmountStr === "0.00" ? "0.00" : (isNaN(parseFloat(rawAmountStr)) ? "0.00" : String(parseFloat(rawAmountStr))))
            : "0.00";

        // Mobile mirrors RequestedAmount into ApprovedAmount/Number on creation
        const payload: any = {
            is_draft: false,
            EmpID: empId,
            Action: action,
            UnitKey: "",
            Currency: "INR",
            ToRemarks: toRemarks,
            ToDynamics: toDynamics,
            // Mirror requested values into approved (matching mobile app behaviour)
            ApprovedAmount: requestedAmountVal,
            ApprovedNumber: requestedNumberVal,
            RequestedAmount: requestedAmountVal,
            RequestedNumber: requestedNumberVal,
            RequestOperation: "EC",
            ApprovedTaxExempt: "0.00",
            RequestedTaxExempt: "0.00",
            // Mobile always sends all three collections even when empty
            ToDependants: toDependants,
            ToAttachments: toAttachments,
            ToMultiLines: toMultiLines,
            ToTransactionDatas: toTransactionDatas,
        };

        return payload;
    };

    const handleSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const { isValid, errors } = await validateForm();
        if (!isValid) {
            // Do not show the blocking modal, just scroll to the first error
            if (errors) {
                const firstErrorField = Object.keys(errors)[0];
                if (firstErrorField) {
                    setTimeout(() => {
                        const el = document.getElementById(`field-${firstErrorField}`) || document.getElementById(`section-${firstErrorField}`);
                        if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "center" });
                            const focusable = el.querySelector("input, select, textarea, button") as HTMLElement;
                            if (focusable) focusable.focus();
                        }
                    }, 100);
                }
            }
            return;
        }

        setIsSubmitting(true);
        setShowLoadingModal(true);
        setLoadingMessage("Validating Reimbursement Request...");

        try {
            const validationPayload = await buildSubmissionObject("V");
            // const finalPayload = JSON.parse(validationPayload)
            const resultAction = await dispatch(createReimbursementRequest(validationPayload));
            console.log("validationPayload", resultAction);
            if (createReimbursementRequest.fulfilled.match(resultAction)) {
                const responseData = resultAction.payload;
                console.log('responseData', responseData);

                let innerErr = responseData?.data?.data?.innerError ?? responseData?.data?.innerError ?? responseData?.innerError;
                if (innerErr && typeof innerErr === 'object' && !Array.isArray(innerErr)) {
                    if (Array.isArray(innerErr.errordetails) && innerErr.errordetails.length > 0) {
                        innerErr = innerErr.errordetails;
                    }
                }

                const allMessages: string[] = [];
                if (Array.isArray(innerErr) && innerErr.length > 0) {
                    innerErr.forEach((e: any) => {
                        const str = typeof e === 'string' ? e : (e?.message || e?.description || String(e));
                        if (str) allMessages.push(str);
                    });
                }
                const rootMsg =
                    responseData?.data?.data?.message ??
                    responseData?.data?.message ??
                    responseData?.message ??
                    responseData?.data?.data?.Message ??
                    responseData?.data?.Message ??
                    responseData?.Message;
                if (rootMsg && typeof rootMsg === 'string') {
                    if (rootMsg.startsWith("/") || (rootMsg.includes("/") && rootMsg.length > 25)) {
                        const parts = rootMsg.split("/").map(p => p.trim()).filter(p => p.length > 0 && !/^[\/\\-]+$/.test(p));
                        parts.forEach(p => {
                            const clean = p.replace(/^\d+[\.\)]\s*/, "").replace(/^[-•*]\s*/, "").trim();
                            if (clean && !allMessages.includes(clean)) allMessages.push(clean);
                        });
                    } else if (!allMessages.includes(rootMsg)) {
                        allMessages.push(rootMsg);
                    }
                }

                const isInfoNotice = (msg: string) => {
                    const m = msg.toLowerCase().trim();
                    return (
                        m.includes("eligiblity amt for incidental charges") ||
                        m.includes("eligibility amt for incidental charges") ||
                        m.includes("request validation is successful") ||
                        m.includes("validation is successful") ||
                        m.includes("saved successfully")
                    );
                };

                const realErrors = allMessages.filter(msg => !isInfoNotice(msg));

                const isSuccess = realErrors.length === 0 || (
                    responseData?.success !== false &&
                    responseData?.data?.success !== false &&
                    responseData?.data?.data?.success !== false
                );

                if (isSuccess && realErrors.length === 0) {
                    const finalSubmissionData = await buildSubmissionObject("C");
                    setReviewData(finalSubmissionData);
                    setShowLoadingModal(false);
                    setIsSubmitting(false);
                    setIsReviewMode(true);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                    setShowLoadingModal(false);
                    setIsSubmitting(false);

                    let finalMsg = realErrors.length > 0
                        ? realErrors.map(b => `• ${b}`).join("\n")
                        : (rootMsg || "Validation failed.");

                    setResponseMessage(finalMsg);
                    setResponseType("error");
                    setShowResponseModal(true);
                }
            } else {
                setShowLoadingModal(false);
                setIsSubmitting(false);
                const errPayload: any = resultAction.payload || resultAction.error?.message;
                const errStr =
                    Api.getErrorMessage(errPayload) ||
                    (typeof errPayload === "string"
                        ? errPayload
                        : (errPayload?.message || errPayload?.data?.message || errPayload?.data?.data?.message || errPayload?.data?.Message || errPayload?.Message || errPayload?.error?.message || errPayload?.error || "Validation failed."));
                setResponseMessage(errStr);
                setResponseType("error");
                setShowResponseModal(true);
            }
        } catch (error: any) {
            console.error("Error during validation:", error);
            setShowLoadingModal(false);
            setIsSubmitting(false);
            const errStr = Api.getErrorMessage(error) || error?.message || "Validation failed. Please verify your input.";
            setResponseMessage(errStr);
            setResponseType("error");
            setShowResponseModal(true);
        }
    };

    const handleConfirmFinalSubmit = async () => {
        setIsSubmitting(true);
        setShowLoadingModal(true);
        setLoadingMessage("Submitting Reimbursement Request...");

        try {
            const resultAction = await dispatch(createReimbursementRequest(reviewData));
            setShowLoadingModal(false);
            setIsSubmitting(false);

            if (createReimbursementRequest.fulfilled.match(resultAction)) {
                const responseData = resultAction.payload;

                let innerErr = responseData?.data?.data?.innerError ?? responseData?.data?.innerError ?? responseData?.innerError;
                if (innerErr && typeof innerErr === 'object' && !Array.isArray(innerErr)) {
                    if (Array.isArray(innerErr.errordetails) && innerErr.errordetails.length > 0) {
                        innerErr = innerErr.errordetails;
                    }
                }

                const allMessages: string[] = [];
                if (Array.isArray(innerErr) && innerErr.length > 0) {
                    innerErr.forEach((e: any) => {
                        const str = typeof e === 'string' ? e : (e?.message || e?.description || String(e));
                        if (str) allMessages.push(str);
                    });
                }
                const rootMsg =
                    responseData?.data?.data?.message ??
                    responseData?.data?.message ??
                    responseData?.message ??
                    responseData?.data?.data?.Message ??
                    responseData?.data?.Message ??
                    responseData?.Message;
                if (rootMsg && typeof rootMsg === 'string') {
                    if (rootMsg.startsWith("/") || (rootMsg.includes("/") && rootMsg.length > 25)) {
                        const parts = rootMsg.split("/").map(p => p.trim()).filter(p => p.length > 0 && !/^[\/\\-]+$/.test(p));
                        parts.forEach(p => {
                            const clean = p.replace(/^\d+[\.\)]\s*/, "").replace(/^[-•*]\s*/, "").trim();
                            if (clean && !allMessages.includes(clean)) allMessages.push(clean);
                        });
                    } else if (!allMessages.includes(rootMsg)) {
                        allMessages.push(rootMsg);
                    }
                }

                const isInfoNotice = (msg: string) => {
                    const m = msg.toLowerCase().trim();
                    return (
                        m.includes("eligiblity amt for incidental charges") ||
                        m.includes("eligibility amt for incidental charges") ||
                        m.includes("request validation is successful") ||
                        m.includes("validation is successful") ||
                        m.includes("saved successfully")
                    );
                };

                const realErrors = allMessages.filter(msg => !isInfoNotice(msg));

                const isSuccess = realErrors.length === 0 || (
                    responseData?.success !== false &&
                    responseData?.data?.success !== false &&
                    responseData?.data?.data?.success !== false
                );

                if (isSuccess && realErrors.length === 0) {
                    setResponseMessage("Reimbursement request submitted successfully!");
                    setResponseType("success");
                    setIsReviewMode(false);
                    setAttachments([]);
                    setRemark("");
                    setShowResponseModal(true);
                } else {
                    let finalMsg = realErrors.length > 0
                        ? realErrors.map(b => `• ${b}`).join("\n")
                        : (rootMsg || "Submission failed.");

                    setResponseMessage(finalMsg);
                    setResponseType("error");
                    setIsReviewMode(false); // Redirect back to form on error
                    setShowResponseModal(true);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                }
            } else {
                const errPayload: any = resultAction.payload || resultAction.error?.message;
                const errStr =
                    Api.getErrorMessage(errPayload) ||
                    (typeof errPayload === "string"
                        ? errPayload
                        : (errPayload?.message || errPayload?.data?.message || errPayload?.data?.data?.message || errPayload?.data?.Message || errPayload?.Message || errPayload?.error?.message || errPayload?.error || "Failed to submit request."));
                setResponseMessage(errStr);
                setResponseType("error");
                setIsReviewMode(false); // Redirect back to form on error
                setShowResponseModal(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        } catch (err: any) {
            setShowLoadingModal(false);
            setIsSubmitting(false);
            const errStr = Api.getErrorMessage(err) || err?.message || "Failed to submit request. Please try again.";
            setResponseMessage(errStr);
            setResponseType("error");
            setIsReviewMode(false); // Redirect back to form on error
            setShowResponseModal(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const initializeLineItem = () => {
        const item: any = {};
        const tableHeaders = dynamicConfig?.configData?.parsedMultilineTableHeaders || dynamicConfig?.parsedMultilineTableHeaders || [];
        const userPayScale =
            dynamicConfig?.employeeDetails?.pay_scale ||
            dynamicConfig?.pay_scale ||
            userData?.details?.pay_scale ||
            userData?.pay_scale ||
            "";

        const userSalary =
            dynamicConfig?.employeeDetails?.basicSalary ||
            dynamicConfig?.basicSalary ||
            userData?.details?.basicSalary ||
            userData?.basicSalary ||
            "";

        tableHeaders.forEach((header: DynamicField) => {
            const fieldName = header.LineCustomerField || header.HeaderCustomerField || "";
            if (fieldName) {
                const isLineReadOnly =
                    isBoolean((header as any).CustFldReadOnly) ||
                    isBoolean(header.ReadOnlyForEmployee) ||
                    isBoolean(header.ReadOnlyForEmp);
                const rawDef = header.DefaultValue || "";

                if (header.ViewOfFieldType === 'L' || fieldName === 'LINUM') {
                    item[fieldName] = String(lineItems.length + 1).padStart(4, '0');
                } else if (isLineReadOnly && !isInvalidDefaultValue(header, rawDef, userPayScale, userSalary)) {
                    item[fieldName] = rawDef;
                } else {
                    item[fieldName] = "";
                }
            }
        });
        return item;
    };

    // Match mobile exactly: AllowDependantFlag must be the string "true"
    const allowDependants = dynamicConfig?.configData?.AllowDependantFlag === "true" || dynamicConfig?.AllowDependantFlag === "true";

    const rawDependents: Dependant[] = (formData.ReimbursementType === "ZLTA" && formData.RequestType === "AC" && refDependents && refDependents.length > 0)
        ? refDependents
        : (
            dynamicConfig?.toDependants ||
            dynamicConfig?.configData?.toDependants ||
            dynamicConfig?.data?.toDependants ||
            []
        );

    // ZOOP excludes Father ("11") and Mother without widow flag ("12" with WidowMotherFlag !== true) (matches mobile)
    const availableDependents: Dependant[] = (rawDependents.length > 0 && formData.ReimbursementType === "ZOOP")
        ? rawDependents.filter((item: any) => item.FamilyMember !== "11" && (item.FamilyMember !== "12" || item.WidowMotherFlag === true))
        : rawDependents;

    // Sync dependents — exact copy of mobile's syncDependents():
    // calls SYNC_REIMBURSEMENT = 'employee-reimbursement/sync-reimbursement-request'
    // with payload { empId } then re-fetches the config.
    const handleSyncDependents = async () => {
        const empId = userData?.details?.empId || userData?.empId || userData?.usrid || "";
        if (isSyncingDependents || !empId) return;
        setIsSyncingDependents(true);
        try {
            const res = await dispatch(syncReimbursementRequest(empId));
            if (syncReimbursementRequest.fulfilled.match(res)) {
                // Re-fetch config so the updated dependents list is loaded (same as mobile)
                if (formData.ReimbursementType && formData.RequestType) {
                    await dispatch(fetchDynamicConfig({
                        ReimbursementType: formData.ReimbursementType,
                        RequestType: formData.RequestType,
                        LTCSchemeCode: formData.LTCScheme || "",
                        EmpID: empId,
                        RequestOperation: "EC",
                    }));
                }
            }
        } finally {
            setIsSyncingDependents(false);
        }
    };

    // Sync reimbursement types & eligibility — exact copy of mobile's syncReimbursementTypes():
    // calls SYNC_REIMBURSEMENT_TYPES = 'employee-reimbursement/sync-reimbursement-types'
    // with payload { empId } then re-fetches masters / types.
    const handleSyncReimbursementTypes = async () => {
        const empId = userData?.details?.empId || userData?.empId || userData?.usrid || "";
        if (isSyncingTypes || !empId) return;
        setIsSyncingTypes(true);
        try {
            const res = await dispatch(syncReimbursementTypes(empId));
            if (syncReimbursementTypes.fulfilled.match(res)) {
                await dispatch(fetchReimbursementTypes());
                await dispatch(fetchReimbursementMasters());
            }
        } catch (e) {
            console.error("Error syncing reimbursement types:", e);
        } finally {
            setIsSyncingTypes(false);
        }
    };

    const allowAttachment = dynamicConfig?.configData?.AllowAttachment !== "false" && dynamicConfig?.AllowAttachment !== "false";

    const isAssocRefStage =
        (formData.ReimbursementType === 'ZLTA' && formData.RequestType !== 'AP') ||
        ((formData.ReimbursementType === 'ZTDA' || formData.ReimbursementType === 'ZVAQ') && (formData.RequestType === 'CA' || formData.RequestType === 'CC'));

    const isAssocRefSectionReady = !isAssocRefStage || !!formData.AssociatedReference;

    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    {/* Header bar */}
                    <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
                        <div>
                            <h3 className="page-title text-dark fw-bold mb-1">
                                <i className="ti ti-receipt me-2 text-primary"></i>
                                Reimbursement Claims &amp; Advances
                            </h3>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={routes.adminDashboard}>Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to={`/${routes.Reimbursement.list}`}>Reimbursement List</Link>
                                    </li>
                                    <li className="breadcrumb-item active">
                                        {isReviewMode ? "Review Reimbursement Request" : "New Reimbursement Request"}
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-md-0 mt-2">
                            {/* Main Top Header Sync button – identical to mobile's screen header sync button */}
                            {!isReviewMode && (
                                <button
                                    type="button"
                                    className="btn btn-outline-primary rounded-pill px-3 py-2 d-flex align-items-center gap-1 shadow-xs"
                                    onClick={handleSyncReimbursementTypes}
                                    disabled={isSyncingTypes}
                                    title="Sync reimbursement types & eligibility from SAP"
                                >
                                    <i className={`ti ${isSyncingTypes ? 'ti-refresh animate-spin' : 'ti-refresh'}`}></i>
                                    <span>{isSyncingTypes ? "Syncing..." : "Sync"}</span>
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-outline-secondary rounded-pill px-3 py-2"
                                onClick={() => navigate(`/${routes.Reimbursement.list}`)}
                            >
                                <i className="ti ti-arrow-left me-1"></i> Back to List
                            </button>
                        </div>
                    </div>

                    {/* Review Mode Banner */}
                    {isReviewMode && (
                        <div className="alert alert-warning mb-4 py-3 px-4 rounded-3 d-flex align-items-center justify-content-between shadow-xs">
                            <div className="d-flex align-items-center gap-2">
                                <i className="ti ti-eye fs-18 text-warning"></i>
                                <span className="fw-semibold text-dark small">
                                    <strong>Review Mode:</strong> Please verify all information below. All fields are locked. Click <strong>Previous</strong> to edit or <strong>Submit</strong> to finalize your request.
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-dark rounded-pill px-3"
                                onClick={() => {
                                    setIsReviewMode(false);
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                            >
                                <i className="ti ti-pencil me-1"></i> Edit Form
                            </button>
                        </div>
                    )}

                    <form onSubmit={handleSubmitForm}>
                        {/* Header Dropdowns (Reimbursement Type, Request Type, LTC Scheme, Block Type) */}
                        <ReimbursementFormHeader
                            formData={formData}
                            handleInputChange={handleInputChange}
                            reimbursementTypes={reimbursementTypes}
                            reimbursementMasters={reimbursementMasters}
                            reimbursementTypesLoading={reimbursementTypesLoading}
                            mastersLoading={mastersLoading}
                            requestTypes={requestTypes}
                            requestTypesLoading={requestTypesLoading}
                            ltcSchemes={ltcSchemes}
                            ltcSchemesLoading={ltcSchemesLoading}
                            blockTypes={blockTypes}
                            blockTypesLoading={blockTypesLoading}
                            validationErrors={validationErrors}
                            getBlockTypeDateRange={getBlockTypeDateRange}
                            readOnly={isReviewMode}
                            associatedRefData={associatedRefData}
                            associatedRefLoading={associatedRefLoading}
                            isAssocRefStage={isAssocRefStage}
                        />

                        {isAssocRefSectionReady && (
                            <>
                                {/* Dynamic Claim Fields Section */}
                                {formData.ReimbursementType && formData.RequestType && (
                                    <DynamicFieldsSection
                                        dynamicConfigLoading={dynamicConfigLoading}
                                        error={error}
                                        dynamicConfig={dynamicConfig}
                                        formData={formData}
                                        handleInputChange={handleInputChange}
                                        validationErrors={validationErrors}
                                        userData={userData}
                                        isReviewMode={isReviewMode}
                                    />
                                )}

                                {/* Dependents Section – shown when AllowDependantFlag === "true" (exact mobile condition) */}
                                {formData.ReimbursementType && formData.RequestType && allowDependants && (
                                    <div
                                        id="section-selectedDependents"
                                        className={`card border-0 shadow-sm rounded-4 p-4 mb-4 ${validationErrors.selectedDependents ? "border border-danger" : ""}`}
                                    >
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <label className="form-label text-dark fw-bold mb-0 fs-14">
                                                <i className="ti ti-users me-2 text-primary"></i>
                                                Dependents <span className="text-danger">*</span>
                                            </label>
                                            <div className="d-flex gap-2">
                                                {/* Sync button – identical to mobile's sync icon */}
                                                {!isReviewMode && !(
                                                    formData.ReimbursementType === 'ZLTA' && formData.RequestType === 'AC'
                                                ) && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary rounded-pill px-2 py-1"
                                                            title="Sync dependents from SAP"
                                                            onClick={handleSyncDependents}
                                                            disabled={isSyncingDependents}
                                                        >
                                                            <i className={`ti ${isSyncingDependents ? 'ti-refresh animate-spin' : 'ti-refresh'} me-1`}></i>
                                                            {isSyncingDependents ? 'Syncing...' : 'Sync'}
                                                        </button>
                                                    )}
                                                {!isReviewMode && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary rounded-pill px-3 py-1"
                                                        onClick={() => setShowDependentsModal(true)}
                                                        disabled={availableDependents.length === 0}
                                                        title={availableDependents.length === 0 ? 'No dependents found – click Sync to load from SAP' : undefined}
                                                    >
                                                        <i className="ti ti-pencil me-1"></i>
                                                        Select Dependents ({selectedDependents.length})
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {validationErrors.selectedDependents && (
                                            <div className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center" role="alert">
                                                <i className="ti ti-alert-circle me-2 fs-16"></i>
                                                <span>{validationErrors.selectedDependents}</span>
                                            </div>
                                        )}

                                        {availableDependents.length === 0 ? (
                                            // No dependents loaded yet — guide user to sync (matches mobile UX)
                                            <div
                                                className="p-3 text-center border border-dashed rounded-3 bg-light"
                                            >
                                                <i className="ti ti-refresh text-muted fs-18 mb-1 d-block"></i>
                                                <span className="text-muted small">
                                                    No dependents found. Click <strong>Sync</strong> to fetch from SAP.
                                                </span>
                                            </div>
                                        ) : selectedDependents.length > 0 ? (
                                            <div className="row g-3">
                                                {selectedDependents.map((dep, idx) => (
                                                    <div key={idx} className="col-md-6 col-lg-4">
                                                        <div className="p-3 border border-success bg-success bg-opacity-10 rounded-3 shadow-xs">
                                                            <div className="d-flex align-items-center justify-content-between mb-1">
                                                                <span className="fw-bold text-dark text-uppercase">{dep.FullName}</span>
                                                                <i className="ti ti-check-circle text-success fs-18"></i>
                                                            </div>
                                                            <div className="text-muted small">
                                                                {dep.Name} • Age: {dep.AgeYears}Y • DOB: {dep.BirthDate}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div
                                                className="p-3 text-center border border-dashed rounded-3 bg-light"
                                                style={{ cursor: isReviewMode ? "default" : "pointer" }}
                                                onClick={() => !isReviewMode && setShowDependentsModal(true)}
                                            >
                                                <span className="text-muted small">
                                                    {isReviewMode ? "No dependents selected" : "Click here to select dependents for this claim"}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* MultiLine Table Items Section */}
                                {formData.ReimbursementType && formData.RequestType && (
                                    <LineItemsSection
                                        dynamicConfig={dynamicConfig}
                                        lineItems={lineItems}
                                        setLineItems={updateLineItemsAndTotal}
                                        onDeleteLine={(index) => updateLineItemsAndTotal(lineItems.filter((_, i) => i !== index))}
                                        readOnly={isReviewMode}
                                        error={validationErrors.lineItems}
                                        availableDependents={availableDependents}
                                        onAddNewLine={() => {
                                            setCurrentLineItem(initializeLineItem());
                                            setEditingLineItemIndex(null);
                                            setShowLineItemModal(true);
                                        }}
                                        onEditLine={(item: any, index: number) => {
                                            setCurrentLineItem(item);
                                            setEditingLineItemIndex(index);
                                            setShowLineItemModal(true);
                                        }}
                                    />
                                )}

                                {/* ── Requested Amount ─────────────────────────────────── */}
                                {!excludeStaticFields && formData.ReimbursementType && formData.RequestType && (
                                    <div className="card border-0 shadow-sm rounded-3 mb-4 mt-2" id="field-RequestedAmount">
                                        <div className="card-body p-3">
                                            <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: 13 }}>
                                                {requestedLabel} <span className="text-danger">*</span>
                                            </label>
                                            <Input
                                                placeholder={`Enter ${requestedLabel}`}
                                                style={{ height: 40, borderRadius: 8, fontWeight: "600", color: "#000" }}
                                                value={formData.RequestedAmount ?? ""}
                                                disabled={isReviewMode || useAmountAtLine}
                                                readOnly={isReviewMode || useAmountAtLine}
                                                onChange={(e) => {
                                                    if (useAmountAtLine) return;
                                                    const val = e.target.value;
                                                    // Allow only numbers and a single decimal point
                                                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                                        handleInputChange({ target: { name: "RequestedAmount", value: val } });
                                                    }
                                                }}
                                            />
                                            {validationErrors.RequestedAmount && (
                                                <div className="text-danger small mt-1">{validationErrors.RequestedAmount}</div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Attachments & Remarks Section */}
                                {formData.ReimbursementType && formData.RequestType && (
                                    <AttachmentsAndRemarksSection
                                        key={`${formData.ReimbursementType}-${formData.RequestType}-${formData.LTCScheme || ""}-${formData.AssociatedReference || ""}`}
                                        userData={userData}
                                        remark={remark}
                                        setRemark={setRemark}
                                        attachments={attachments}
                                        setAttachments={setAttachments}
                                        allowAttachment={allowAttachment}
                                        readOnly={isReviewMode}
                                    />
                                )}

                                {/* Submit Actions Row */}
                                {formData.ReimbursementType && formData.RequestType && (
                                    <div className="row mt-4 mb-4">
                                        <div className="col-12">
                                            <div className="d-flex justify-content-end gap-3">
                                                {isReviewMode ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary px-4 py-2.5 rounded-pill fw-bold fs-14"
                                                            onClick={() => {
                                                                setIsReviewMode(false);
                                                                window.scrollTo({ top: 0, behavior: "smooth" });
                                                            }}
                                                            disabled={isSubmitting}
                                                        >
                                                            <i className="ti ti-arrow-left me-1"></i> Previous
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn text-white px-5 py-2.5 rounded-pill shadow-sm fw-bold fs-14"
                                                            style={{ backgroundColor: "#A93226" }}
                                                            onClick={handleConfirmFinalSubmit}
                                                            disabled={isSubmitting}
                                                        >
                                                            {isSubmitting ? "Submitting..." : "Submit"}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary px-4 py-2.5 rounded-pill fw-bold fs-14"
                                                            onClick={() => navigate(`/${routes.Reimbursement.list}`)}
                                                            disabled={isSubmitting}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="submit"
                                                            className="btn text-white px-5 py-2.5 rounded-pill shadow-sm fw-bold fs-14"
                                                            style={{ backgroundColor: "#A93226" }}
                                                            disabled={isSubmitting || dynamicConfigLoading}
                                                        >
                                                            Review
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </form>
                </div>
            </div>

            {/* Subcomponents Modals */}
            <DependentsModal
                open={showDependentsModal}
                onClose={() => setShowDependentsModal(false)}
                dependents={availableDependents}
                selectedDependents={selectedDependents}
                onConfirm={(selected: Dependant[]) => {
                    setSelectedDependents(selected);
                    if (selected.length > 0) {
                        setValidationErrors((prev) => {
                            const updated = { ...prev };
                            delete updated.selectedDependents;
                            return updated;
                        });
                    }
                }}
            />

            <LineItemsModal
                open={showLineItemModal}
                onClose={() => {
                    setShowLineItemModal(false);
                    setCurrentLineItem(null);
                    setEditingLineItemIndex(null);
                }}
                currentLineItem={currentLineItem}
                setCurrentLineItem={setCurrentLineItem}
                editingLineItemIndex={editingLineItemIndex}
                dynamicConfig={dynamicConfig}
                onSave={() => {
                    let updated: any[];
                    if (editingLineItemIndex !== null && editingLineItemIndex >= 0) {
                        updated = [...lineItems];
                        updated[editingLineItemIndex] = currentLineItem;
                    } else {
                        updated = [...lineItems, currentLineItem];
                    }
                    updateLineItemsAndTotal(updated);
                    setShowLineItemModal(false);
                    setCurrentLineItem(null);
                    setEditingLineItemIndex(null);
                }}
                availableDependents={availableDependents}
                reimbursementType={formData.ReimbursementType || formData.reimbursementType}
            />

            {/* Response Alert Modal */}
            <Modal
                title={responseType === "success" ? "Success" : "Error"}
                open={showResponseModal}
                onOk={() => {
                    setShowResponseModal(false);
                    if (responseType === "success") {
                        navigate(`/${routes.Reimbursement.list}`);
                    }
                }}
                onCancel={() => setShowResponseModal(false)}
            >
                <p className="mb-0" style={{ whiteSpace: "pre-line" }}>{responseMessage}</p>
            </Modal>

            {/* Full Page Loader */}
            <FullPageLoader show={showLoadingModal} message={loadingMessage} />
        </>
    );
};

export default ReimbursementCreationModal;
